import EventDetailView from '@/components/feature/event/event-detail-view';
import EventMainView from '@/components/feature/event/event-main-view';
import { useOrientation } from '@/hooks';
import useBaseSize from '@/hooks/useBaseSize';
import { useConfigStore } from '@/stores';
import type { EventDetailSlide, EventSlide } from '@/types/event';
import { getHSize, getSize } from '@/utils/size';
import { useMemo, useState } from 'react';

export default function EventPage() {
    const lotteEvents = useConfigStore((state) => state.config?.server?.building?.lotteEvents);
    const buildingMenus = useConfigStore((state) => state.config?.server?.buildingMenus);
    const language = useConfigStore((state) => state.config?.ui?.language);
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const eventTitle = buildingMenus?.[language]?.find((item) => item.menuType === 'EVENT')?.name || '';
    const eventDescription = buildingMenus?.[language]?.find((item) => item.menuType === 'EVENT')?.description || '';
    const { isHorizontal } = useOrientation();
    const { baseWidth, baseHeight } = useBaseSize();
    const isHalfScreen = screenMode === 'split';

    // State for detail view
    const [showDetailView, setShowDetailView] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [detailItems, setDetailItems] = useState<EventDetailSlide[]>([]);

    // Transform mock data to EventSlide format and group into slides
    const events: EventSlide[] = useMemo(() => {
        return (
            lotteEvents?.events.map((event) => ({
                id: event.shpgHhlghAditNo,
                header: event.shopNowHeader,
                description: event.shopNowNm,
                imageUrl: event.imageUrl,
                list: event.cntsList || null,
            })) || []
        );
    }, [lotteEvents]);

    // Handler for showing detail view
    const handleItemClick = (id: string) => {
        const categoryEvents = events.filter((event) => event.id === id);
        const headerDetail = `${categoryEvents?.[0]?.header}` + '\n' + categoryEvents?.[0]?.description || '';
        setSelectedCategory(headerDetail);

        // Extract all list items from category events as EventDetailSlide[]
        const allListItems = categoryEvents.flatMap((event) => event.list || []);

        setDetailItems(allListItems);
        setShowDetailView(true);
    };

    // Handler for going back to main view
    const handleBackToMain = () => {
        setShowDetailView(false);
        setSelectedCategory('');
        setDetailItems([]);
    };

    // Group events into slides for horizontal layout
    const eventSlidesHorizontal: EventSlide[][] = useMemo(() => {
        const slides: EventSlide[][] = [];
        for (let i = 0; i < events.length; i += 6) {
            slides.push(events.slice(i, i + 6));
        }
        return slides;
    }, [events, isHalfScreen]);

    // Group events into slides for vertical layout
    const eventSlidesVertical: EventSlide[][] = useMemo(() => {
        const slides: EventSlide[][] = [];
        const itemsPerSlide = isHalfScreen ? 2 : 6; // 6 items for vertical layout
        for (let i = 0; i < events.length; i += itemsPerSlide) {
            slides.push(events.slice(i, i + itemsPerSlide));
        }
        return slides;
    }, [events, isHalfScreen]);

    // Group detail items into slides for horizontal layout
    const detailSlidesHorizontal: EventDetailSlide[][] = useMemo(() => {
        const slides: EventDetailSlide[][] = [];
        for (let i = 0; i < detailItems.length; i += 5) {
            slides.push(detailItems.slice(i, i + 5));
        }
        return slides;
    }, [detailItems, isHalfScreen]);

    // Group detail items into slides for vertical layout
    const detailSlidesVertical: EventDetailSlide[][] = useMemo(() => {
        const slides: EventDetailSlide[][] = [];
        const itemsPerSlide = isHalfScreen ? 3 : 6; // 6 items for vertical layout
        for (let i = 0; i < detailItems.length; i += itemsPerSlide) {
            slides.push(detailItems.slice(i, i + itemsPerSlide));
        }
        return slides;
    }, [detailItems, isHalfScreen]);

    // Calculate top margin based on screenMode
    const topMargin = screenMode === 'fullscreen' ? '0' : isHorizontal ? `${getHSize(171, baseHeight)}` : `${getHSize(868, baseHeight)}`;

    return (
        <>
            <div
                className="flex h-full w-full flex-col bg-[var(--color-bg-gray-f9)]"
                style={{
                    marginTop: topMargin,
                    ...(screenMode !== 'fullscreen' && {
                        height: isHorizontal ? `${getHSize(829, baseHeight)}` : `${getHSize(872, baseHeight)}`,
                        maxWidth: `${getSize(1920, baseWidth)}`,
                        overflow: 'hidden',
                    }),
                }}
            >
                {/* Header */}
                <header
                    className={`widescreen:pt-[calc(56*var(--vh-unit))] widescreen:fullscreen:pb-[calc(14*var(--vh-unit))] fullscreen:pb-[calc(64*var(--vh-unit))] widescreen:pb-[calc(14*var(--vh-unit))] widescreen:pl-[calc(67*var(--vw-unit))] widescreen:pr-[calc(68*var(--vw-unit))] widescreen:border-none contrast:border-b-[calc(1*var(--vh-unit))] contrast:border-[var(--color-secondary-black-light)] px-[calc(56*var(--vw-unit))] pt-[calc(86*var(--vh-unit))] pb-[calc(4*var(--vh-unit))] text-center`}
                >
                    <div
                        className="focus-border widescreen:gap-[calc(16*var(--vh-unit))] flex flex-col gap-[calc(10*var(--vh-unit))]"
                        tabIndex={1}
                        data-region-nav
                        data-region-order={1}
                        data-interactive
                        aria-label={eventTitle + ' ' + eventDescription}
                    >
                        <h1 className="widescreen:text-[calc(56*var(--vh-unit))] widescreen:font-medium widescreen:leading-[calc(60*var(--vh-unit))] truncate text-center text-2xl text-[calc(60*var(--vh-unit))] leading-[calc(90*var(--vh-unit))] font-bold text-[color:var(--color-text-main01)]">
                            {eventTitle}
                        </h1>
                        <p className="widescreen:text-[calc(26*var(--vh-unit))] widescreen:font-medium widescreen:leading-[calc(30*var(--vh-unit))] truncate text-center text-[calc(28*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-medium text-[color:var(--color-text-main01)]">
                            {eventDescription}
                        </p>
                    </div>
                </header>
                {showDetailView ? (
                    <EventDetailView
                        detailSlidesHorizontal={detailSlidesHorizontal}
                        detailSlidesVertical={detailSlidesVertical}
                        onBack={handleBackToMain}
                        isHorizontal={isHorizontal}
                        selectedCategory={selectedCategory}
                    />
                ) : (
                    <EventMainView
                        eventSlidesHorizontal={eventSlidesHorizontal}
                        eventSlidesVertical={eventSlidesVertical}
                        onItemClick={handleItemClick}
                        isHorizontal={isHorizontal}
                    />
                )}
            </div>
        </>
    );
}
